import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Spinner, Alert, Table, ProgressBar, Form, Modal } from "react-bootstrap";
import {
  Calendar,
  CalendarDays,
  Users,
  MapPin,
  Monitor,
  BookOpen,
  Video,
  VideoOff,
  Clock,
  CheckCircle,
  UserCheck,
  AlertCircle,
  XCircle,
  Star,
  ClipboardList,
  Brain,
  Code,
  MessageSquare,
  LayoutGrid,
  FileText,
  Download,
  Sparkles,
  BarChart3,
  BriefcaseBusiness,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { enterLiveClass } from "../../utils/liveClassRouting";
import { getCurrentUser } from "../../utils/auth";
import {
  AppCard,
  AppCardContent,
  AppBadge,
  AppButton,
  StatusBadge,
  PageHeader,
  ConfirmModal,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  UserAvatar,
} from "../ui";
import TrainerTodaySession from "../Syllabus/TrainerTodaySession";
import TrainerSmartSessions from "../Syllabus/SmartSessions/TrainerSmartSessions";
import BatchAnalytics from "./BatchAnalytics";
import BatchVideosPage from "../Videos/BatchVideosPage";
import BatchMaterialsPage from "../Materials/BatchMaterialsPage";
import CommonToaster from "../Common/CommonToaster";
import PreMeetingCheck from "../Classroom/PreMeetingCheck";
import { startSmartSession, adminListSessions } from "../../utils/smartLearning";
import "./InstructorBatchDetail.scss";

/* ── Star Rating Component ── */
function StarRating({ value, onChange, disabled, size = 20 }) {
  return (
    <div className="ibd-star-rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          className={`ibd-star ${n <= value ? "ibd-star--filled" : "ibd-star--empty"} ${disabled ? "ibd-star--disabled" : ""}`}
          onClick={() => !disabled && onChange(n)}
          fill={n <= value ? "#f59e0b" : "none"}
          stroke={n <= value ? "#f59e0b" : "currentColor"}
        />
      ))}
    </div>
  );
}

/* ── Feedback Modal ── */
function FeedbackModal({ show, onHide, student, batchId, existingReview, onSaved, setToast }) {
  const [performance, setPerformance] = useState(0);
  const [participation, setParticipation] = useState(0);
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show && existingReview) {
      setPerformance(existingReview.performance || 0);
      setParticipation(existingReview.participation || 0);
      setRemark(existingReview.remark || "");
    } else if (show) {
      setPerformance(0);
      setParticipation(0);
      setRemark("");
    }
  }, [show, existingReview]);

  const handleSubmit = async () => {
    if (!performance || !participation) {
      setToast({ show: true, message: "Please set both ratings.", type: "warning" });
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("/student-reviews", {
        batchId,
        studentId: student._id,
        performance,
        participation,
        remark,
      });
      onSaved(student._id, { performance, participation, remark, studentId: student._id });
      setToast({ show: true, message: "Feedback saved!", type: "success" });
      onHide();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to save.", type: "error" });
    }
    setSaving(false);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: "1rem" }}>
          <MessageSquare size={18} />
          Feedback for {student?.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ibd-feedback-field">
          <label className="ibd-feedback-label">Performance</label>
          <p className="ibd-feedback-hint">How well does the student grasp and apply concepts?</p>
          <StarRating value={performance} onChange={setPerformance} disabled={saving} size={24} />
        </div>
        <div className="ibd-feedback-field">
          <label className="ibd-feedback-label">Participation</label>
          <p className="ibd-feedback-hint">How actively does the student engage in class, ask doubts?</p>
          <StarRating value={participation} onChange={setParticipation} disabled={saving} size={24} />
        </div>
        <div className="ibd-feedback-field">
          <label className="ibd-feedback-label">Remark <span className="text-muted fw-normal">(optional)</span></label>
          <Form.Control
            as="textarea"
            rows={2}
            maxLength={200}
            placeholder="e.g. Strong in DSA, needs more practice in SQL..."
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            disabled={saving}
            className="ibd-remark-input"
          />
          <small className="text-muted">{remark.length}/200</small>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <AppButton variant="ghost" onClick={onHide} disabled={saving}>Cancel</AppButton>
        <AppButton onClick={handleSubmit} disabled={saving}>
          {saving ? <><Spinner size="sm" animation="border" className="me-1" /> Saving...</> : existingReview ? "Update Feedback" : "Save Feedback"}
        </AppButton>
      </Modal.Footer>
    </Modal>
  );
}

/* ── Instructor Students Review Tab ── */
function InstructorStudentsReview({ batchId, setToast }) {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [reviews, setReviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [feedbackStudent, setFeedbackStudent] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [studRes, revRes] = await Promise.all([
        apiClient.get(`/student-reviews/batch/${batchId}/students`),
        apiClient.get(`/student-reviews/batch/${batchId}`),
      ]);
      setStudents(studRes.data.students || []);
      const revMap = {};
      for (const r of revRes.data.reviews || []) {
        revMap[r.studentId] = r;
      }
      setReviews(revMap);
    } catch {
      setStudents([]);
    }
    setLoading(false);
  }, [batchId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReviewSaved = (studentId, review) => {
    setReviews((prev) => ({ ...prev, [studentId]: review }));
  };

  if (loading) {
    return <div className="text-center py-4"><Spinner size="sm" animation="border" /></div>;
  }

  if (students.length === 0) {
    return <p className="text-muted mt-3" style={{ fontSize: "0.85rem" }}>No active students in this batch.</p>;
  }

  return (
    <>
      <AppCard className="ibd-info-card">
        <AppCardContent>
          <h6 className="ibd-section-title" style={{ marginBottom: 12 }}>
            <Users size={18} />
            Students
            <small className="text-muted" style={{ fontWeight: 400, fontSize: "0.75rem", marginLeft: 8 }}>
              ({students.length} active)
            </small>
          </h6>
          <div className="table-responsive">
            <Table size="sm" hover className="mb-0 ibd-review-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th className="text-center">AI Attempts</th>
                  <th className="text-center">Coding Practice</th>
                  <th className="text-center">Assignments</th>
                  <th style={{ width: 190 }}></th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => {
                  const review = reviews[s._id];
                  return (
                    <tr key={s._id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <UserAvatar name={s.name} size="sm" />
                          <span className="fw-medium" style={{ fontSize: "0.85rem" }}>{s.name}</span>
                        </div>
                      </td>
                      <td className="text-center align-middle">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <Brain size={14} className="text-muted" />
                          <span style={{ fontSize: "0.85rem" }}>{s.aiAttempts || 0}</span>
                        </div>
                      </td>
                      <td className="text-center align-middle">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <Code size={14} className="text-muted" />
                          <span style={{ fontSize: "0.85rem" }}>{s.codingSessions || 0}</span>
                        </div>
                      </td>
                      <td className="text-center align-middle">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <ClipboardList size={14} className="text-muted" />
                          <span style={{ fontSize: "0.85rem" }}>{s.assignmentsSubmitted || 0}</span>
                        </div>
                      </td>
                      <td className="align-middle text-end">
                        <div className="d-inline-flex align-items-center gap-1">
                          {/* The server refuses a student outside this
                              instructor's batches, so this link is safe to
                              render from any roster they can already see. */}
                          <AppButton
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/i/students/${s._id}/portfolio`)}
                            className="d-inline-flex align-items-center gap-1"
                          >
                            <BriefcaseBusiness size={13} />
                            Portfolio
                          </AppButton>
                          <AppButton
                            size="sm"
                            variant={review ? "ghost" : "outline"}
                            onClick={() => setFeedbackStudent(s)}
                            className="d-inline-flex align-items-center gap-1"
                          >
                            <MessageSquare size={13} />
                            {review ? "Edit" : "Feedback"}
                          </AppButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </AppCardContent>
      </AppCard>

      {feedbackStudent && (
        <FeedbackModal
          show={!!feedbackStudent}
          onHide={() => setFeedbackStudent(null)}
          student={feedbackStudent}
          batchId={batchId}
          existingReview={reviews[feedbackStudent._id]}
          onSaved={handleReviewSaved}
          setToast={setToast}
        />
      )}
    </>
  );
}

/**
 * InstructorBatchDetail
 *
 * Instructor-facing batch detail page accessible at /i/batch/:batchId.
 * Shows batch header info, Start/End class controls, and TrainerTodaySession (syllabus execution widget).
 */
const InstructorBatchDetail = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // "Open Smart Session" from the dashboard deep-links to ?tab=smart-sessions.
  const initialTab = searchParams.get("tab") || "overview";
  const [activeTab, setActiveTab] = useState(initialTab);

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // ── Attendance state ──
  const [liveSession, setLiveSession] = useState(null);
  const [attendanceStats, setAttendanceStats] = useState(null);

  // ── Overview data: syllabus progress + materials ──
  const [syllabus, setSyllabus] = useState(null);
  const [materials, setMaterials] = useState([]);

  // ── Smart Learning Flow gate for this batch's course ──
  const [smartLearningOn, setSmartLearningOn] = useState(false);
  // Smart sessions drive the Overview (progress/upcoming/recent) when the flow
  // is on — the legacy BatchSyllabusSession is not used for these batches.
  const [smartSessions, setSmartSessions] = useState([]);

  useEffect(() => {
    const fetchBatch = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await apiClient.get(`/batches/${batchId}`);
        setBatch(data.batch || data);
      } catch {
        setError("Failed to load batch details.");
      } finally {
        setLoading(false);
      }
    };
    fetchBatch();

    // Refetch when page is restored from bfcache (e.g. returning from classroom)
    const handlePageShow = (e) => {
      if (e.persisted) fetchBatch();
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [batchId]);

  // ── Overview data: syllabus progress (sessions) + batch materials ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [sylRes, matRes] = await Promise.all([
        apiClient.get(`/syllabus/batch/${batchId}/syllabus`).catch(() => null),
        apiClient.get(`/materials/batch/${batchId}`).catch(() => null),
      ]);
      if (cancelled) return;
      if (sylRes?.data) setSyllabus(sylRes.data);
      if (matRes?.data?.data) {
        const bm = matRes.data.data.batchMaterials || [];
        const cm = matRes.data.data.courseMaterials || [];
        const merged = [...bm, ...cm]
          .filter((m) => m.status !== "failed")
          .sort(
            (a, b) =>
              new Date(b.sessionDate || b.createdAt) - new Date(a.sessionDate || a.createdAt)
          );
        setMaterials(merged);
      }
    })();
    return () => { cancelled = true; };
  }, [batchId]);

  // Probe smartLearningFlow whenever the batch's course is known.
  // Resilient by design: a transient probe failure must NOT hide the Smart
  // Sessions tab. Previously a single failed/one-shot probe collapsed the flag
  // to `false`, so sessions "disappeared" until a reload re-probed. We now only
  // flip the flag on a definitive response and retry a few times on error.
  useEffect(() => {
    const courseId = batch?.course?._id || batch?.course;
    if (!courseId) {
      setSmartLearningOn(false);
      return undefined;
    }
    let cancelled = false;
    let attempt = 0;
    const probe = () => {
      apiClient
        .get("/smart-learning/feature-status", { params: { courseId } })
        .then(({ data }) => {
          if (!cancelled) setSmartLearningOn(!!data?.enabled);
        })
        .catch(() => {
          // Network/transient error → don't hide the tab; retry with backoff.
          if (!cancelled && attempt < 3) {
            attempt += 1;
            setTimeout(() => { if (!cancelled) probe(); }, 700 * attempt);
          }
        });
    };
    probe();
    return () => { cancelled = true; };
  }, [batch?.course]);

  // When the smart flow is on, load its sessions to drive the Overview.
  useEffect(() => {
    const courseId = batch?.course?._id || batch?.course;
    if (!smartLearningOn || !courseId) {
      setSmartSessions([]);
      return undefined;
    }
    let cancelled = false;
    adminListSessions({ courseId, batchId })
      .then((data) => { if (!cancelled) setSmartSessions(data?.sessions || []); })
      .catch(() => { if (!cancelled) setSmartSessions([]); });
    return () => { cancelled = true; };
  }, [smartLearningOn, batch?.course, batchId]);

  // Fetch live attendance when batch is live, poll every 15s
  useEffect(() => {
    if (!batch?.isLive) {
      setLiveSession(null);
      return;
    }
    const fetchLive = async () => {
      try {
        const { data } = await apiClient.get(`/attendance/batch/${batchId}/live`);
        setLiveSession(data.session);
      } catch { /* ignore */ }
    };
    fetchLive();
    const interval = setInterval(fetchLive, 15000);
    return () => clearInterval(interval);
  }, [batchId, batch?.isLive]);

  // Fetch attendance stats once
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await apiClient.get(`/attendance/batch/${batchId}/stats`);
        setAttendanceStats(data);
      } catch { /* ignore */ }
    };
    fetchStats();
  }, [batchId]);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
          timeZone: "Asia/Kolkata",
        })
      : "—";

  const formatTime = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    return `${hours12}:${String(minutes).padStart(2, "0")} ${period}`;
  };

  const formatClock = (d) =>
    d
      ? new Date(d).toLocaleTimeString("en-IN", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZone: "Asia/Kolkata",
        })
      : "";

  const formatDateTime = (d) => (d ? `${formatDate(d)} • ${formatClock(d)}` : "");

  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const batchTimeLabel =
    batch?.startTime && batch?.endTime
      ? `${formatTime(batch.startTime)} – ${formatTime(batch.endTime)}`
      : "";

  const handleDownloadMaterial = async (material) => {
    try {
      const { data } = await apiClient.get(`/materials/${material._id}/download`);
      if (data?.data?.url) {
        window.open(data.data.url, "_blank");
      }
    } catch {
      setToast({ show: true, message: "Failed to download material.", type: "error" });
    }
  };

  // ── Derived Overview data ──
  // When the smart flow is on, the Overview is driven by SmartSessions; the
  // legacy BatchSyllabusSession is not used for these batches. Both are mapped
  // to one normalized shape so the cards/progress render identically.
  const normSessions = useMemo(() => {
    if (smartLearningOn) {
      return (smartSessions || [])
        .filter((s) => s.status !== "cancelled")
        .map((s) => ({
          id: s._id,
          title: s.title,
          description: s.description || "",
          completed: s.status === "completed",
          completedAt: s.endedAt ? new Date(s.endedAt) : null,
          plannedDate: null,
          durationMinutes: s.plannedDurationMinutes || 0,
          order: (s.moduleNumber || 0) * 1000 + (s.order || 0),
          isSmart: true,
        }));
    }
    return (syllabus?.sessions || []).map((s) => ({
      id: s.dayNumber,
      title: s.title,
      description:
        s.trainerNotes ||
        (s.topics || []).map((t) => t.name).filter(Boolean).join(", "),
      completed: s.status === "completed",
      completedAt: s.endedAt || s.actualDate ? new Date(s.endedAt || s.actualDate) : null,
      plannedDate: s.plannedDate || null,
      durationMinutes: 0,
      order: s.dayNumber,
      isSmart: false,
    }));
  }, [smartLearningOn, smartSessions, syllabus]);

  const upcomingSession = useMemo(
    () => normSessions.find((s) => !s.completed) || null,
    [normSessions]
  );

  const recentSessions = useMemo(
    () =>
      normSessions
        .filter((s) => s.completed)
        .sort(
          (a, b) =>
            (b.completedAt?.getTime() || 0) - (a.completedAt?.getTime() || 0) ||
            b.order - a.order
        )
        .slice(0, 4),
    [normSessions]
  );

  const recentMaterials = useMemo(() => materials.slice(0, 3), [materials]);

  const progress = useMemo(() => {
    const total = normSessions.length;
    if (!total) return null;
    const completed = normSessions.filter((s) => s.completed).length;
    return { completed, total, percent: Math.round((completed / total) * 100) };
  }, [normSessions]);

  const activity = useMemo(() => {
    const items = [];
    for (const s of normSessions) {
      if (s.completed && s.completedAt) {
        items.push({
          type: "session",
          date: s.completedAt,
          text: (
            <>
              Session <strong>“{s.title}”</strong> completed
            </>
          ),
        });
      }
    }
    for (const m of materials) {
      const when = m.createdAt || m.sessionDate;
      if (!when) continue;
      items.push({
        type: "material",
        date: new Date(when),
        text: (
          <>
            Material <strong>“{m.title}”</strong> uploaded
          </>
        ),
      });
    }
    return items
      .filter((i) => i.date && !isNaN(i.date.getTime()))
      .sort((a, b) => b.date - a.date)
      .slice(0, 6);
  }, [normSessions, materials]);

  const canStartClass =
    batch &&
    (batch.mode === "Online" ||
      batch.mode === "Both" ||
      batch.meetingLink ||
      batch.zoomMeetingId);

  // ── Start / End Meeting ──
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showStatusWarning, setShowStatusWarning] = useState(false);
  // Set when a smart session's "Start Class" triggers the system-check modal.
  const [pendingStartSession, setPendingStartSession] = useState(null);
  // Prefilled topic for a regular (non-smart) class start.
  const [pendingTopic, setPendingTopic] = useState("");
  // Set when a syllabus day's "Start class" triggers the system-check modal.
  const [pendingSyllabusDay, setPendingSyllabusDay] = useState(null);
  // Bumped after a syllabus day starts its class, so the Syllabus tab re-reads.
  const [syllabusVersion, setSyllabusVersion] = useState(0);

  // Open the pre-meeting check for a regular class start (status-gated).
  const requestStartClass = (topic = "") => {
    if (!batch) return;
    if (batch.status !== "Ongoing") {
      setShowStatusWarning(true);
      return;
    }
    setPendingStartSession(null);
    setPendingSyllabusDay(null);
    setPendingTopic(topic || "");
    setShowStartConfirm(true);
  };

  const handleStartMeeting = (sessionTopic) => {
    apiClient
      .post(`/batches/${batchId}/start-meeting`, { sessionTopic: sessionTopic || "" })
      .then(({ data }) => {
        setToast({ show: true, message: "Meeting started successfully.", type: "success" });

        // Embedded classroom (Zoom v2 / platform-hosted / Grats Meet) or, for
        // legacy v1 and Zoho, the provider's own link — see utils/liveClassRouting.
        enterLiveClass(batchId, data, batch);

        setBatch((prev) => ({ ...prev, isLive: true }));
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || "Failed to start meeting.";
        setToast({ show: true, message: msg, type: "danger" });
      });
  };

  // Start on a syllabus day while no class is running: the same system check as
  // Start Class, then ONE call that opens the class (server-side, the same code
  // as /start-meeting) and starts the day — a day never starts without a class.
  const requestStartDay = (session) => {
    if (!batch) return;
    if (batch.status !== "Ongoing") {
      setShowStatusWarning(true);
      return;
    }
    setPendingStartSession(null);
    setPendingTopic("");
    setPendingSyllabusDay(session);
    setShowStartConfirm(true);
  };

  const handleStartSyllabusDay = async (session, sessionTopic) => {
    try {
      const { data } = await apiClient.post(
        `/syllabus/batch/${batchId}/syllabus/${session.dayNumber}/start`,
        { sessionTopic: sessionTopic || session.title || "" }
      );
      setToast({ show: true, message: data.classStarted ? "Class started." : "Day started.", type: "success" });
      setSyllabusVersion((v) => v + 1);
      if (data.classStarted) {
        setBatch((prev) => ({ ...prev, isLive: true }));
        enterLiveClass(batchId, data, batch);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to start class.";
      setToast({ show: true, message: msg, type: "danger" });
    }
  };

  // Start a smart session = the exact normal Start Class flow (Zoom + attendance
  // via /start-meeting), then flip THIS session live (manual mode = no second
  // meeting provisioned) so its resources unlock and its recording maps to it.
  const handleStartSmartSession = async (session, sessionTopic) => {
    if (!session) return;
    try {
      const { data } = await apiClient.post(`/batches/${batchId}/start-meeting`, {
        sessionTopic: sessionTopic || session.title || "",
      });
      try {
        await startSmartSession(session._id, {
          mode: "manual",
          batchId,
          // Whatever /start-meeting actually used — hardcoding "zoom" here made
          // every Zoho and Grats Meet session claim a provider it never ran on.
          meetingProvider: data.meetingProvider || "zoom",
          meetingJoinUrl: `/i/classroom/${batchId}`,
        });
      } catch { /* non-fatal: the class meeting is already live */ }

      setToast({ show: true, message: "Class started.", type: "success" });
      setBatch((prev) => ({ ...prev, isLive: true }));

      enterLiveClass(batchId, data, batch);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to start class.";
      setToast({ show: true, message: msg, type: "danger" });
    }
  };

  const handleEndMeeting = () => {
    if (!batch) return;
    setShowEndConfirm(false);
    apiClient
      .post(`/batches/${batchId}/end-meeting`)
      .then(() => {
        setToast({ show: true, message: "Meeting ended successfully.", type: "success" });
        setBatch((prev) => ({ ...prev, isLive: false }));
      })
      .catch(() => {
        setToast({ show: true, message: "Failed to end meeting.", type: "danger" });
      });
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="my-5 text-center">
        {error}
      </Alert>
    );
  }

  if (!batch) {
    return (
      <div className="text-muted text-center my-5">Batch not found.</div>
    );
  }

  return (
    <div className="ibd-page">
      {/* ── Page Header ── */}
      <PageHeader
        backButton
        title={batch.name}
        description={batch.course?.name || "—"}
        actions={
          <div className="ibd-header-actions">
            {/* Smart-flow batches start class only from the Smart Sessions tab. */}
            {canStartClass && !batch.isLive && !smartLearningOn && (
              <AppButton
                variant="default"
                size="sm"
                onClick={() => requestStartClass(upcomingSession?.title || "")}
              >
                <Video size={16} />
                Start Class
              </AppButton>
            )}
            {canStartClass && batch.isLive && (
              <AppButton
                variant="destructive"
                size="sm"
                onClick={() => setShowEndConfirm(true)}
              >
                <VideoOff size={16} />
                End Class
              </AppButton>
            )}
          </div>
        }
      />

      {/* ── Batch Hero Card ── */}
      <AppCard className="ibd-hero">
        <AppCardContent className="ibd-hero__main">
          <div className="ibd-hero__identity">
            <div className="ibd-hero__icon">
              <BookOpen size={26} />
            </div>
            <div className="ibd-hero__titles">
              <h2 className="ibd-hero__name">{batch.name}</h2>
              <span className="ibd-hero__course">{batch.course?.name || "—"}</span>
              <div className="ibd-hero__status">
                <StatusBadge status={String(batch.status || "").toLowerCase()}>
                  {batch.status}
                </StatusBadge>
                {batch.isLive && (
                  <AppBadge variant="success" className="ibd-live-badge">
                    <span className="ibd-live-dot" />
                    LIVE
                  </AppBadge>
                )}
              </div>
            </div>
          </div>

          <div className="ibd-stats">
            <div className="ibd-stat">
              <span className="ibd-stat__label"><Calendar size={14} /> Duration</span>
              <span className="ibd-stat__value">
                {formatDate(batch.startDate)} – {formatDate(batch.endDate)}
              </span>
            </div>
            {batchTimeLabel && (
              <div className="ibd-stat">
                <span className="ibd-stat__label"><Clock size={14} /> Schedule</span>
                <span className="ibd-stat__value">{batchTimeLabel}</span>
              </div>
            )}
            <div className="ibd-stat">
              <span className="ibd-stat__label"><Users size={14} /> Students</span>
              <span className="ibd-stat__value">{batch.strength ?? "—"} students</span>
            </div>
            <div className="ibd-stat">
              <span className="ibd-stat__label"><Monitor size={14} /> Mode</span>
              <span className="ibd-stat__value">{batch.mode || "—"}</span>
            </div>
            {batch.room && (
              <div className="ibd-stat">
                <span className="ibd-stat__label"><MapPin size={14} /> Room</span>
                <span className="ibd-stat__value">{batch.room}</span>
              </div>
            )}
          </div>

          {progress && (
            <div className="ibd-progress-panel">
              <div className="ibd-progress-head">
                <span className="ibd-progress-title">Batch Progress</span>
                <span className="ibd-progress-pct">{progress.percent}% Completed</span>
              </div>
              <div className="ibd-progress-bar">
                <div
                  className="ibd-progress-bar__fill"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
              <span className="ibd-progress-sessions">
                {progress.completed} / {progress.total} Sessions
              </span>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── Live Attendance (visible when class is live) ── */}
      {batch.isLive && liveSession && ((
        <AppCard className="ibd-info-card">
          <AppCardContent>
            <h6 className="ibd-section-title" style={{ marginBottom: 12 }}>
              <UserCheck size={18} />
              Live Attendance
              <AppBadge variant="success" style={{ marginLeft: 8, fontSize: '0.7rem' }}>
                {(liveSession.participants || []).length} joined
              </AppBadge>
            </h6>
            {(liveSession.participants || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>No students have joined yet.</p>
            ) : (
              <div className="ibd-live-participants">
                {(liveSession.participants || []).map((p, idx) => {
                  // If last heartbeat within 60s → active; else stale (might have left)
                  const lastHb = p.lastHeartbeat ? new Date(p.lastHeartbeat) : null;
                  const isActive = lastHb && (Date.now() - lastHb.getTime() < 60000);
                  const hasLeft = !!p.leftAt;
                  return (
                    <div key={idx} className={`ibd-participant-chip ${hasLeft ? 'ibd-chip-left' : isActive ? '' : 'ibd-chip-stale'}`}>
                      {hasLeft
                        ? <XCircle size={14} className="text-danger" />
                        : isActive
                          ? <CheckCircle size={14} className="text-success" />
                          : <AlertCircle size={14} className="text-warning" />
                      }
                      <span>{p.name || 'Student'}</span>
                      <small className="text-muted">
                        {hasLeft
                          ? `left ${new Date(p.leftAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}`
                          : new Date(p.joinedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })
                        }
                      </small>
                    </div>
                  );
                })}
              </div>
            )}
          </AppCardContent>
        </AppCard>
      ))}

      {/* ── Tabs: Overview, Analytics, Students, Attendance, Sessions, Materials, Syllabus/Smart Sessions ── */}
      <Tabs value={activeTab} onChange={setActiveTab} className="ibd-tabs mt-3">
        <TabsList overflow>
          <TabsTrigger value="overview"><LayoutGrid size={14} className="me-1" /> Overview</TabsTrigger>
          <TabsTrigger value="analytics"><BarChart3 size={14} className="me-1" /> Analytics</TabsTrigger>
          <TabsTrigger value="students"><Users size={14} className="me-1" /> Students</TabsTrigger>
          <TabsTrigger value="attendance"><UserCheck size={14} className="me-1" /> Attendance</TabsTrigger>
          <TabsTrigger value="sessions"><Video size={14} className="me-1" /> Sessions</TabsTrigger>
          <TabsTrigger value="materials"><BookOpen size={14} className="me-1" /> Materials</TabsTrigger>
          {/* Smart-flow batches replace the legacy Syllabus tab with Smart Sessions. */}
          {smartLearningOn ? (
            <TabsTrigger value="smart-sessions"><Sparkles size={14} className="me-1" /> Smart Sessions</TabsTrigger>
          ) : (
            <TabsTrigger value="syllabus"><ClipboardList size={14} className="me-1" /> Syllabus</TabsTrigger>
          )}
        </TabsList>

        {/* ── Overview ── */}
        <TabsContent value="overview">
          <div className="ibd-overview-grid">
            {/* Left column */}
            <div className="ibd-overview-col">
              {/* Upcoming Session */}
              <AppCard className="ibd-ov-card">
                <AppCardContent>
                  <div className="ibd-section-head">
                    <h6 className="ibd-section-title">Upcoming Session</h6>
                  </div>
                  {upcomingSession ? (
                    <div className="ibd-up">
                      <div className="ibd-up__icon">
                        <CalendarDays size={22} />
                      </div>
                      <div className="ibd-up__body">
                        <span className="ibd-next-badge">Next Session</span>
                        <h5 className="ibd-up__title">{upcomingSession.title}</h5>
                        <div className="ibd-up__meta">
                          {upcomingSession.plannedDate && (
                            <span><Calendar size={14} /> {formatDate(upcomingSession.plannedDate)}</span>
                          )}
                          {upcomingSession.durationMinutes > 0 && (
                            <span><Clock size={14} /> {upcomingSession.durationMinutes} min</span>
                          )}
                          {batchTimeLabel && (
                            <span><Clock size={14} /> {batchTimeLabel}</span>
                          )}
                        </div>
                        {upcomingSession.description && (
                          <p className="ibd-up__desc">{upcomingSession.description}</p>
                        )}
                        {batch.isLive ? (
                          <AppButton
                            size="sm"
                            className="ibd-up__btn"
                            onClick={() => navigate(`/i/classroom/${batchId}`)}
                          >
                            <Video size={15} /> Join Live Class
                          </AppButton>
                        ) : smartLearningOn ? (
                          <AppButton
                            size="sm"
                            variant="outline"
                            className="ibd-up__btn"
                            onClick={() => setActiveTab("smart-sessions")}
                          >
                            <Sparkles size={15} /> Start from Smart Sessions
                          </AppButton>
                        ) : canStartClass ? (
                          <AppButton
                            size="sm"
                            className="ibd-up__btn"
                            onClick={() => requestStartClass(upcomingSession.title)}
                          >
                            <Video size={15} /> Start Class
                          </AppButton>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <p className="ibd-empty-text">
                      {normSessions.length > 0
                        ? "All sessions completed 🎉"
                        : "No sessions scheduled yet."}
                    </p>
                  )}
                </AppCardContent>
              </AppCard>

              {/* Batch Materials */}
              <AppCard className="ibd-ov-card">
                <AppCardContent>
                  <div className="ibd-section-head">
                    <h6 className="ibd-section-title">Batch Materials</h6>
                    {materials.length > 0 && (
                      <button type="button" className="ibd-view-all" onClick={() => setActiveTab("materials")}>
                        View all
                      </button>
                    )}
                  </div>
                  {recentMaterials.length > 0 ? (
                    <div className="ibd-mat-list">
                      {recentMaterials.map((m) => (
                        <div className="ibd-mat-item" key={m._id}>
                          <div className="ibd-mat__icon"><FileText size={18} /></div>
                          <div className="ibd-mat__info">
                            <span className="ibd-mat__title">{m.title}</span>
                            <span className="ibd-mat__meta">{m.fileType} • {formatFileSize(m.fileSize)}</span>
                          </div>
                          <span className="ibd-mat__date">{formatDate(m.sessionDate || m.createdAt)}</span>
                          <button
                            type="button"
                            className="ibd-mat__download"
                            title="Download"
                            onClick={() => handleDownloadMaterial(m)}
                          >
                            <Download size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="ibd-empty-text">No materials uploaded yet.</p>
                  )}
                </AppCardContent>
              </AppCard>
            </div>

            {/* Right column */}
            <div className="ibd-overview-col">
              {/* Recent Sessions */}
              <AppCard className="ibd-ov-card">
                <AppCardContent>
                  <div className="ibd-section-head">
                    <h6 className="ibd-section-title">Recent Sessions</h6>
                    {normSessions.length > 0 && (
                      <button
                        type="button"
                        className="ibd-view-all"
                        onClick={() => setActiveTab(smartLearningOn ? "smart-sessions" : "syllabus")}
                      >
                        View all
                      </button>
                    )}
                  </div>
                  {recentSessions.length > 0 ? (
                    <div className="ibd-rs-list">
                      {recentSessions.map((s) => (
                        <div className="ibd-rs-item" key={s.id}>
                          <div className="ibd-rs__icon"><CheckCircle size={18} /></div>
                          <div className="ibd-rs__info">
                            <span className="ibd-rs__title">{s.title}</span>
                            <span className="ibd-rs__meta">
                              {s.completedAt ? formatDate(s.completedAt) : ""}
                              {s.completedAt && batchTimeLabel ? ` • ${batchTimeLabel}` : ""}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="ibd-rs__view"
                            onClick={() => setActiveTab(smartLearningOn ? "smart-sessions" : "sessions")}
                          >
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="ibd-empty-text">No sessions completed yet.</p>
                  )}
                </AppCardContent>
              </AppCard>

              {/* Batch Activity */}
              <AppCard className="ibd-ov-card">
                <AppCardContent>
                  <div className="ibd-section-head">
                    <h6 className="ibd-section-title">Batch Activity</h6>
                  </div>
                  {activity.length > 0 ? (
                    <div className="ibd-act-list">
                      {activity.map((a, i) => (
                        <div className="ibd-act-item" key={i}>
                          <span className={`ibd-act__dot ibd-act__dot--${a.type}`} />
                          <div className="ibd-act__body">
                            <span className="ibd-act__text">{a.text}</span>
                            <span className="ibd-act__time">{formatDateTime(a.date)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="ibd-empty-text">No recent activity.</p>
                  )}
                </AppCardContent>
              </AppCard>
            </div>
          </div>
        </TabsContent>

        {/* ── Analytics ── */}
        <TabsContent value="analytics">
          <BatchAnalytics batchId={batchId} />
        </TabsContent>

        {/* ── Students & Reviews ── */}
        <TabsContent value="students">
          <InstructorStudentsReview batchId={batchId} setToast={setToast} />
        </TabsContent>

        {/* ── Attendance Overview ── */}
        <TabsContent value="attendance">
          {attendanceStats && attendanceStats.totalSessions > 0 ? (
            <AppCard className="ibd-info-card">
              <AppCardContent>
                <h6 className="ibd-section-title" style={{ marginBottom: 12 }}>
                  <Users size={18} />
                  Attendance Overview
                  <small className="text-muted" style={{ fontWeight: 400, fontSize: '0.75rem', marginLeft: 8 }}>
                    ({attendanceStats.totalSessions} session{attendanceStats.totalSessions !== 1 ? 's' : ''})
                  </small>
                </h6>
                <Table size="sm" hover responsive className="mb-0" style={{ fontSize: '0.82rem' }}>
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th style={{ width: 90 }}>Present</th>
                      <th style={{ width: 170 }}>Attendance</th>
                      <th style={{ width: 110 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceStats.students.map((s) => {
                      const lastDetail = s.details && s.details.length > 0 ? s.details[s.details.length - 1] : null;
                      return (
                        <tr key={s.studentId}>
                          <td>{s.name}</td>
                          <td>{s.attended} / {s.total}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <ProgressBar
                                now={s.percentage}
                                variant={s.percentage >= 75 ? 'success' : s.percentage >= 50 ? 'warning' : 'danger'}
                                style={{ height: 6, flex: 1 }}
                              />
                              <small style={{ minWidth: 32 }}>{s.percentage}%</small>
                            </div>
                          </td>
                          <td>
                            {lastDetail ? (
                              <span className={`ibd-att-status ibd-att-${lastDetail.status}`}>
                                {lastDetail.status === 'present'
                                  ? <><CheckCircle size={12} /> Present</>
                                  : lastDetail.status === 'partial'
                                    ? <><AlertCircle size={12} /> Partial ({lastDetail.activeMins}m)</>
                                    : lastDetail.status === 'dropped'
                                      ? <><XCircle size={12} /> Dropped</>
                                      : 'Pending'
                                }
                              </span>
                            ) : (
                              <span className="text-muted">Absent</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </AppCardContent>
            </AppCard>
          ) : (
            <p className="text-muted mt-3" style={{ fontSize: '0.85rem' }}>No attendance sessions recorded yet.</p>
          )}
        </TabsContent>

        {/* ── Session Recordings ── */}
        <TabsContent value="sessions">
          <div className="ibd-syllabus-section">
            <BatchVideosPage batchId={batchId} role="instructor" currentUserId={getCurrentUser()?._id || getCurrentUser()?.id} />
          </div>
        </TabsContent>

        {/* ── Class Materials ── */}
        <TabsContent value="materials">
          <div className="ibd-syllabus-section">
            <BatchMaterialsPage batchId={batchId} courseId={batch?.course?._id || batch?.course} role="instructor" currentUserId={getCurrentUser()?._id || getCurrentUser()?.id} />
          </div>
        </TabsContent>

        {/* ── Syllabus Progress (legacy flow only) ── */}
        {!smartLearningOn && (
          <TabsContent value="syllabus">
            <div className="ibd-syllabus-section">
              <TrainerTodaySession
                key={syllabusVersion}
                batchId={batchId}
                batchTiming={{
                  startTime: batch.startTime,
                  endTime: batch.endTime,
                }}
                classLive={!!batch.isLive}
                onStartDay={requestStartDay}
              />
            </div>
          </TabsContent>
        )}

        {smartLearningOn && (
          <TabsContent value="smart-sessions">
            <div className="ibd-syllabus-section">
              <TrainerSmartSessions
                courseId={batch?.course?._id || batch?.course}
                batchId={batchId}
                onStartSession={(s) => {
                  if (batch.status !== 'Ongoing') {
                    setShowStatusWarning(true);
                    return;
                  }
                  setPendingStartSession(s);
                  setShowStartConfirm(true);
                }}
                onStartLiveClass={() => {
                  if (batch.status !== 'Ongoing') {
                    setShowStatusWarning(true);
                    return;
                  }
                  setPendingStartSession(null);
                  setShowStartConfirm(true);
                }}
              />
            </div>
          </TabsContent>
        )}

      </Tabs>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <PreMeetingCheck
        show={showStartConfirm}
        role="instructor"
        batchName={batch?.name}
        batchId={batchId}
        confirmText="Start Class"
        defaultTopic={pendingStartSession?.title || pendingSyllabusDay?.title || pendingTopic || ""}
        onReady={(topic) => {
          setShowStartConfirm(false);
          if (pendingStartSession) {
            const s = pendingStartSession;
            setPendingStartSession(null);
            handleStartSmartSession(s, topic);
          } else if (pendingSyllabusDay) {
            const day = pendingSyllabusDay;
            setPendingSyllabusDay(null);
            handleStartSyllabusDay(day, topic);
          } else {
            handleStartMeeting(topic);
          }
          setPendingTopic("");
        }}
        onCancel={() => {
          setShowStartConfirm(false);
          setPendingStartSession(null);
          setPendingSyllabusDay(null);
          setPendingTopic("");
        }}
      />

      <ConfirmModal
        show={showStatusWarning}
        onHide={() => setShowStatusWarning(false)}
        onConfirm={() => setShowStatusWarning(false)}
        title="Cannot Start Class"
        message={`This batch is in "${batch?.status}" status. Ask admin to set it in active/ongoing status to start class.`}
        confirmText="OK"
        cancelText="Close"
        variant="warning"
      />

      <ConfirmModal
        show={showEndConfirm}
        onHide={() => setShowEndConfirm(false)}
        onConfirm={handleEndMeeting}
        title="End Class?"
        message="This will end the meeting for all participants. Are you sure you want to end the class?"
        confirmText="End Class"
        cancelText="Cancel"
        variant="warning"
      />
    </div>
  );
};

export default InstructorBatchDetail;
